import { observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { ArchiveDocumentFn } from '@/features/documents'
import type { Document } from '@/features/documents/Document'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'

/**
 * Confirms and permanently deletes documents from a job.
 */
export class DeleteJobDocumentsDialogViewModel {
  private readonly _prompt

  @observable private accessor _documents: Document[] = []

  readonly modal = new ModalViewModel()

  constructor(
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _archiveDocument: ArchiveDocumentFn,
  ) {
    this._prompt = new AsyncPrompt<boolean>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Asks the user to confirm, and deletes the documents once they do.
   *
   * @returns Whether the documents were deleted.
   */
  async prompt(documents: Document[], signal?: AbortSignal) {
    this.modal.close()

    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this._documents = documents
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Deletes the documents and resolves the prompt.
   */
  delete = task.resolved(async () => {
    await Promise.all(
      this._documents.map((d) => this._archiveDocument(d)),
    ).catch(this._createAlertErrorHandler('Deleting the documents failed'))

    this._prompt.respond(true)
    this.modal.close()
  })
}
